import { useMemo, useState } from "react";
import { useTable } from "react-table";
import {
  capitalizeLetter,
  defaultDateDisplay,
  defaultDateOnlyDisplay,
  formatCurrency,
} from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { POExtra } from "../POExtra";
import { DeleteIcon } from "../../../../assets/svgIcons";
import "../../../../assets/styles/base/globalPage.scss";
import { usePOShareDialog } from "../POShareDialog/usePOShareDialog";
import secondaryComponents from "../..";

export const usePurchaseOrderTable = ({ purchaseOrders }) => {
  const navigate = useNavigate();
  const [selectedPODetails, setSelectedPODetails] = useState(null);
  const fullfilmentColor = {
    CLOSED: "#19AE57",
    OPEN: "#F7AD3C",
  };
  const { handleUpdateFulfillment } = usePOShareDialog({});

  const columns = useMemo(
    () => [
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateDisplay(value) : "N/A",
      },
      {
        Header: "PO Date",
        accessor: "po_date",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateOnlyDisplay(value) : "N/A",
      },
      {
        Header: "PO Number",
        accessor: "po_number",
        Cell: ({ cell: { value }, row }) => (
          <Link
            to={pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
              id: row?.original?.id,
            })}
            className="purple--text smallest-text-size"
          >
            {value}
          </Link>
        ),
      },
      {
        Header: "Supplier",
        accessor: "supplier",
        Cell: ({ cell: { value } }) => (
          <Link
            to={pathConstants.INVENTORY_SUPPLIERS_DETAILS({
              id: value?.id,
            })}
            className="purple--text smallest-text-size"
          >
            {value?.name || "N/A"}
          </Link>
        ),
      },
      {
        Header: "Store",
        accessor: "stores",
        Cell: ({ cell: { value } }) =>
          value?.map((store) => store?.name)?.join(", ") || "N/A",
      },
      {
        Header: "Total Amount",
        accessor: "total_item_amount",
        Cell: ({ cell: { value }, row }) =>
          formatCurrency({
            value: value,
            currencyCode: row?.original?.stores?.[0]?.currency_code,
          }),
      },
      {
        Header: "Actions",
        accessor: "fulfillment_state",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center" style={{ gap: "10px" }}>
            {row?.original?.status === "ACCEPTED" ? (
              <primaryComponents.FancySelectField
                placeholder={"Update status"}
                options={[
                  { name: "Closed", id: "CLOSED", bgColor: "#19AE57" },
                  { name: "Open", id: "OPEN", bgColor: "#F7AD3C" },
                ]}
                nameKey={"name"}
                idKey={"id"}
                bgKey={"bgColor"}
                style={{ height: "40px" }}
                selectedItem={{
                  label: capitalizeLetter(value?.replace("_", " ")),
                  bgColor: fullfilmentColor[value],
                }}
                onChange={(item) => {
                  handleUpdateFulfillment(row?.original, item?.value);
                }}
              />
            ) : null}

            {row?.original?.status === "DRAFT" ? (
              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey={"can_create_or_update_purchase_order"}
              >
                <primaryComponents.Button
                  className="btn btn--primary smallBtn"
                  onClick={() => {
                    navigate(
                      pathConstants.INVENTORY_PURCHASE_ORDER_EDIT({
                        id: row?.original?.id,
                      }),
                    );
                  }}
                >
                  Update PO
                </primaryComponents.Button>
              </secondaryComponents.NavigateWithPermission>
            ) : null}

            {row?.original?.status === "ACCEPTED" ||
            row?.original?.status === "REJECTED" ||
            row?.original?.status === "IN_REVIEW" ? (
              <primaryComponents.Button
                className="btn btn--primary smallBtn"
                onClick={() => {
                  navigate(
                    pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
                      id: row?.original?.id,
                    }),
                  );
                }}
              >
                {row?.original?.status === "IN_REVIEW" ? "Review" : "View PO"}
              </primaryComponents.Button>
            ) : null}

            {row?.original?.status !== "ACCEPTED" ? (
              <span
                className="cursor-pointer"
                onClick={() => setSelectedPODetails(row?.original)}
              >
                <DeleteIcon />
              </span>
            ) : null}
            <POExtra
              PODetails={row?.original}
              btnStyles={{ width: "40px", height: "32px" }}
            />
          </span>
        ),
      },
    ],
    [],
  );

  const instance = useTable({
    columns,
    data: purchaseOrders || [],
  });

  return {
    instance,
    selectedPODetails,
    setSelectedPODetails,
  };
};
